import { useLocalStorage } from '@mantine/hooks';
import ApartmentIcon from '@mui/icons-material/Apartment';
import CloseIcon from '@mui/icons-material/Close';
import DevicesOtherIcon from '@mui/icons-material/DevicesOther';
import GroupIcon from '@mui/icons-material/Group';
import InfoIcon from '@mui/icons-material/Info';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PersonIcon from '@mui/icons-material/Person';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
    Badge,
    Box,
    Button,
    Divider,
    IconButton,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Stack,
    SwipeableDrawer,
    Switch,
    Tooltip,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';

import NavigationLink from 'kerberus-components/src/Components/Link/NavigationLink';

import { MenuItemProps } from './AppBar/AppBarProfileMenu';

interface RequestBreakdown {
    company: number;
    group: number;
    instance: number;
}

interface MobileDrawerProps {
    admin?: boolean;
    avatarComponent?: React.ReactNode;
    fullName: string;
    hasAdminAccess?: boolean;
    menuItems: Record<string, MenuItemProps[]>;
    onAppsClick?: () => void;
    onClose: () => void;
    onOpen: () => void;
    open: boolean;
    requestBreakdown?: RequestBreakdown;
    showAppsButton: boolean;
    title?: string;
    totalRequests?: number;
}

export default function MobileDrawer({
    admin = false,
    avatarComponent,
    fullName,
    hasAdminAccess,
    menuItems,
    onAppsClick,
    onClose,
    onOpen,
    open,
    requestBreakdown,
    showAppsButton,
    title,
    totalRequests = 0,
}: Readonly<MobileDrawerProps>) {
    const [showInfoButton, setShowInfoButton] = useLocalStorage({ defaultValue: true, key: 'showInfoButton' });
    const theme = useTheme();
    const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));
    const iOS = typeof navigator !== 'undefined' && /iPad|iPhone|iPod/.test(navigator.userAgent);

    const handleInfoButtonToggle = () => {
        setShowInfoButton(!showInfoButton);
    };

    // Calculate total requests count
    const totalRequestsCount = requestBreakdown
        ? requestBreakdown.company + requestBreakdown.group + requestBreakdown.instance
        : totalRequests;

    // Component to render request breakdown
    const renderRequestBreakdown = () => {
        if (!requestBreakdown || totalRequestsCount === 0) {
            return null;
        }

        return (
            <List dense sx={{ py: 0 }}>
                {requestBreakdown.company > 0 && (
                    <ListItem sx={{ py: 0.5 }}>
                        <ListItemIcon sx={{ minWidth: 36 }}>
                            <ApartmentIcon color="primary" fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primary={`Company Requests: ${requestBreakdown.company.toString()}`} />
                    </ListItem>
                )}
                {requestBreakdown.group > 0 && (
                    <ListItem sx={{ py: 0.5 }}>
                        <ListItemIcon sx={{ minWidth: 36 }}>
                            <GroupIcon color="primary" fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primary={`Group Requests: ${requestBreakdown.group.toString()}`} />
                    </ListItem>
                )}
                {requestBreakdown.instance > 0 && (
                    <ListItem sx={{ py: 0.5 }}>
                        <ListItemIcon sx={{ minWidth: 36 }}>
                            <LocationOnIcon color="primary" fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primary={`Instance Requests: ${requestBreakdown.instance.toString()}`} />
                    </ListItem>
                )}
            </List>
        );
    };

    // Common drawer content
    // Don't render anything on desktop
    if (isDesktop) {
        return null;
    }

    // Only render on mobile devices
    return (
        <SwipeableDrawer
            anchor="right"
            disableBackdropTransition={!iOS}
            disableDiscovery={iOS}
            onClose={onClose}
            onOpen={onOpen}
            open={open}
        >
            <Box
                role="presentation"
                sx={{
                    maxWidth: '100vw',
                    paddingBottom: 'var(--safe-area-inset-bottom, 0px)',
                    paddingLeft: 'var(--safe-area-inset-left, 0px)',
                    paddingRight: 'var(--safe-area-inset-right, 0px)',
                    paddingTop: 'var(--safe-area-inset-top, 0px)',
                    width: 280,
                }}
            >
                {/* User Info */}
                <Box sx={{ alignItems: 'flex-start', display: 'flex', justifyContent: 'space-between', p: 2 }}>
                    <Stack direction="row" spacing={1} sx={{ alignItems: 'center', maxWidth: 'calc(100% - 40px)' }}>
                        {avatarComponent || (
                            <PersonIcon
                                sx={{
                                    color: 'primary.main',
                                    flexShrink: 0,
                                    height: 40,
                                    width: 40,
                                }}
                            />
                        )}
                        <Box sx={{ minWidth: 0 }}>
                            <Typography
                                noWrap
                                sx={{
                                    color: 'primary.main',
                                    fontWeight: 'medium',
                                }}
                                variant="body1"
                            >
                                {fullName}
                            </Typography>
                            {title ? (
                                <Typography
                                    noWrap
                                    sx={{
                                        color: 'text.secondary',
                                        display: 'block',
                                    }}
                                    variant="caption"
                                >
                                    {title}
                                </Typography>
                            ) : null}
                        </Box>
                    </Stack>
                    <IconButton onClick={onClose} sx={{ flexShrink: 0 }}>
                        <CloseIcon />
                    </IconButton>
                </Box>

                <Divider />

                <Stack
                    direction="row"
                    sx={{ alignItems: 'center', display: 'flex', justifyContent: 'space-between', px: 2, py: 1 }}
                >
                    {/* Refresh Page */}
                    <Box sx={{ alignItems: 'center', display: 'flex', justifyContent: 'flex-start' }}>
                        <Button
                            color="primary"
                            onClick={() => window.location.reload()}
                            size="small"
                            startIcon={<RefreshIcon />}
                            sx={{ fontSize: '10px', textTransform: 'none' }}
                            variant="outlined"
                        >
                            Refresh
                        </Button>
                    </Box>
                    {/* Info Toggle */}
                    <Box sx={{ alignItems: 'center', display: 'flex', justifyContent: 'flex-end' }}>
                        <Box sx={{ alignItems: 'center', display: 'flex', flexDirection: 'column', mr: 1 }}>
                            <Tooltip placement="top" title="Display additional info">
                                <InfoIcon sx={{ color: showInfoButton ? 'mlBlue.main' : 'gray' }} />
                            </Tooltip>
                            <Typography
                                sx={{
                                    color: showInfoButton ? 'mlBlue.main' : 'gray',
                                    fontSize: '10px',
                                }}
                                variant="caption"
                            >
                                {showInfoButton ? 'Show' : 'Hide'}
                            </Typography>
                        </Box>
                        <Switch checked={showInfoButton} color="info" onChange={handleInfoButtonToggle} size="small" />
                    </Box>
                </Stack>

                <Divider />

                {/* Request Breakdown Section */}
                {!admin && requestBreakdown && totalRequestsCount > 0 ? (
                    <>
                        <Box sx={{ p: 2, pb: 1 }}>
                            <Typography color="primary" sx={{ fontWeight: 'bold', mb: 1 }} variant="overline">
                                Pending Requests ({totalRequestsCount})
                            </Typography>
                            {renderRequestBreakdown()}
                        </Box>
                        <Divider />
                    </>
                ) : null}

                {/* Apps Button */}
                {showAppsButton ? (
                    <>
                        <Box sx={{ pt: 1, px: 2 }}>
                            <Button
                                color="primary"
                                fullWidth
                                onClick={onAppsClick}
                                startIcon={<DevicesOtherIcon />}
                                sx={{ mb: 1 }}
                                variant="outlined"
                            >
                                Download App
                            </Button>
                        </Box>
                        <Divider />
                    </>
                ) : null}

                {/* Menu Items */}
                <Box sx={{ p: 2 }}>
                    {Object.entries(menuItems).map(([category, items]) => (
                        <Box key={category} sx={{ mb: 2 }}>
                            <Typography
                                color="primary"
                                sx={{ display: 'block', fontWeight: 'bold', mb: 1 }}
                                variant="overline"
                            >
                                {category}
                            </Typography>

                            <Stack
                                direction="row"
                                sx={{
                                    flexWrap: 'wrap',
                                    gap: 1,
                                }}
                            >
                                {items.map((item) => {
                                    if (item.adminAccess && !hasAdminAccess) {
                                        return null;
                                    }

                                    return (
                                        <NavigationLink admin={item.admin} key={item.label} to={item.to}>
                                            <Badge
                                                badgeContent={item.totalRequests ? totalRequests : 0}
                                                color="warning"
                                                sx={{
                                                    '& .MuiBadge-badge': {
                                                        right: 15,
                                                    },
                                                }}
                                            >
                                                <Button
                                                    color="primary"
                                                    onClick={() => {
                                                        if (item.handleClick) {
                                                            item.handleClick();
                                                        }
                                                        onClose();
                                                    }}
                                                    startIcon={item.icon}
                                                    sx={{
                                                        borderRadius: 2,
                                                        justifyContent: 'flex-start',
                                                        mb: 1,
                                                        minWidth: 'calc(50% - 8px)',
                                                        textTransform: 'none',
                                                    }}
                                                    variant="outlined"
                                                >
                                                    {item.label}
                                                </Button>
                                            </Badge>
                                        </NavigationLink>
                                    );
                                })}
                            </Stack>
                        </Box>
                    ))}
                </Box>
            </Box>
        </SwipeableDrawer>
    );
}
